import { type ReactNode, memo } from 'react';

import {
  adjustFractionDigits,
  type ConvertibleUnit,
  format,
  formatCurrency,
  formatDate,
  type FormatDateOptions,
  formatLong,
  type FormatOptions,
  type Unit,
  units,
} from '@dynatrace-sdk/units';

import {
  formatCurrencyDefault,
  formatDateDefault,
} from '../../../core/utils/default-formatters.js';
import { isDev } from '../../../core/utils/is-node-environment.js';
import { convertToNumberIfPureNumber } from '../../../core/utils/string-utils.js';
import type { CellContext, ColumnDefBase } from '../hooks/useTable/types.js';
import type { DataTableV2DisplayColumnDef } from '../public.api.js';

/**
 * Options to format a currency (incl. locale and properties accepted by Intl.NumberFormat).
 * @public
 */
export interface FormatterCurrencyOptions
  extends Omit<Intl.NumberFormatOptions, 'currency' | 'style'> {
  /**
   * The ISO 4217 currency code, such as 'USD' or 'EUR', to which the currency will be formatted.
   */
  currency: Intl.NumberFormatOptions['currency'];

  /**
   * The locale according to which the currency will be formatted.
   */
  locale?: string | string[];

  /**
   * Shortens the number, e.g. 1500 → 1.5k
   */
  abbreviate?: boolean;
}

/**
 * Definition type for the DataTableV2 column definition formatter options
 * @public
 */
export type DataTableV2CellFormatter =
  | FormatOptions<Unit, ConvertibleUnit>
  | FormatDateOptions
  | FormatterCurrencyOptions;

/**
 * Defines the options passed to the format function.
 * @public
 */
export type DataTableV2CellFormatterColumnDef = {
  /** Allows formatter options from the `@dynatrace-sdk/units` package to be set. */
  formatter?: DataTableV2CellFormatter;
};

/** Memoized renderer for date formats */
const ColumnCellDateFormatMemo = memo(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (props: { value: any; formatter?: FormatDateOptions }) => {
    return props.value ? formatDate(Number(props.value), props.formatter) : '';
  },
);

/** Memoized renderer for currency formats */
const ColumnCellCurrencyFormatMemo = memo(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (props: { value: any; formatter?: FormatterCurrencyOptions }) => {
    if (
      props.value === null ||
      props.value === undefined ||
      isNaN(props.value)
    ) {
      // value is not a valid number
      return '';
    }

    const { currency, ...options } = props.formatter ?? {};
    if (!currency) {
      // no currency (e.g. 'EUR', 'USD') provided → default format without currency symbol
      return formatCurrencyDefault(Number(props.value), options);
    }

    return formatCurrency(Number(props.value), currency, options);
  },
);

/** Memoized renderer for fallback formats */
const ColumnCellFormatMemo = memo(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (props: { value: any; formatter?: FormatDateOptions }) => {
    return format(Number(props.value), props.formatter);
  },
);

const ColumnCellFormatLongMemo = memo(
  (props: {
    value: string | number | bigint;
    formatter?: Pick<
      FormatOptions<Unit, ConvertibleUnit>,
      | 'locale'
      | 'maximumFractionDigits'
      | 'maximumSignificantDigits'
      | 'minimumFractionDigits'
      | 'minimumSignificantDigits'
      | 'useGrouping'
    >;
  }) => {
    return formatLong(props.value, props.formatter);
  },
);

/**
 * Function that formats the cell value based on the formatter specified in the column definition
 * @param columnDef - the table column definition
 *
 * @returns the formatted cell value
 */
function formatColumnCell<TData, TValue>(
  columnDef: DataTableV2DisplayColumnDef<TData, TValue>,
): ((props: CellContext<TData, TValue>) => ReactNode) | undefined {
  if (
    columnDef.columnType === 'sparkline' ||
    columnDef.columnType === 'meterbar' ||
    columnDef.columnType === 'gantt' ||
    columnDef.columnType === 'log-content' ||
    columnDef.columnType === 'markdown'
  ) {
    return;
  }

  if (columnDef.columnType === 'date') {
    return ({ getValue }) => (
      <ColumnCellDateFormatMemo
        value={getValue()}
        formatter={columnDef.formatter}
      />
    );
  }

  if (columnDef.columnType === 'currency') {
    return ({ getValue }) => (
      <ColumnCellCurrencyFormatMemo
        value={getValue()}
        formatter={columnDef.formatter}
      />
    );
  }

  if (columnDef.columnType === 'long') {
    return ({ getValue }) => {
      const value = getValue();
      if (
        typeof value === 'string' ||
        typeof value === 'bigint' ||
        typeof value === 'number'
      ) {
        return (
          <ColumnCellFormatLongMemo
            value={value}
            formatter={columnDef.formatter}
          />
        );
      }
      return String(value);
    };
  }

  return ({ getValue }) => (
    <ColumnCellFormatMemo value={getValue()} formatter={columnDef.formatter} />
  );
}

/**
 * Updates the cell renderer with the formatter from the column definition
 * @param columnDef - column definition prop
 *
 * @returns the column definition with the formatter applied
 * @internal
 */
export function applyColumnFormatters<TData, TValue>(
  columnDef: DataTableV2DisplayColumnDef<TData, TValue>,
): { cell?: ColumnDefBase<TData, TValue>['cell'] } {
  if (
    columnDef.columnType === 'sparkline' ||
    columnDef.columnType === 'meterbar' ||
    columnDef.columnType === 'gantt' ||
    columnDef.columnType === 'log-content' ||
    columnDef.columnType === 'markdown'
  ) {
    return {};
  }

  if (columnDef.formatter) {
    return {
      cell: formatColumnCell(columnDef),
    };
  }

  return {};
}

/**
 * Formats the given value based on formatter options from the column definition.
 * For 'sparkline', 'meterbar', 'gantt', and 'log-content', returns an empty string.
 * If there is no other formatter possible then returns a stringified value.
 * @param value - the cell value to format.
 * @param column - column definition prop where `columnType` and `formatter` are relevant.
 *
 * @returns the formatted cell value
 * @internal
 */
export function formatCellValue<TData, TValue>(
  value: TValue,
  column: DataTableV2DisplayColumnDef<TData, TValue>,
): string {
  if (
    column.columnType === 'sparkline' ||
    column.columnType === 'meterbar' ||
    column.columnType === 'gantt' ||
    column.columnType === 'log-content' ||
    column.columnType === 'markdown'
  ) {
    return '';
  }

  if (value === null || value === undefined) {
    if (isDev()) {
      // eslint-disable-next-line no-console
      console.warn(`The value '${value}' can not be formatted!`);
    }
    return '';
  }

  const cleanedValue =
    typeof value === 'number' || value instanceof Date
      ? value
      : Number.isNaN(+value)
      ? null
      : convertToNumberIfPureNumber(value as string);

  if (
    column.formatter &&
    column.columnType !== 'date' &&
    column.columnType !== 'currency' &&
    typeof cleanedValue === 'number'
  ) {
    const formatOptions = column.formatter;
    return format(cleanedValue, formatOptions);
  }

  if (column.columnType === 'bit' && typeof cleanedValue === 'number') {
    const formatOptions = { input: units.data.bit };
    return format(cleanedValue, formatOptions);
  }

  if (column.columnType === 'number' && typeof cleanedValue === 'number') {
    const formatOptions = column.formatter;
    if (formatOptions) {
      return format(cleanedValue, formatOptions);
    }
    return adjustFractionDigits(cleanedValue);
  }

  if (
    column.columnType === 'long' &&
    (typeof value === 'string' ||
      typeof value === 'bigint' ||
      typeof value === 'number')
  ) {
    return formatLong(value, column.formatter);
  }

  if (
    column.columnType === 'date' &&
    (typeof cleanedValue === 'number' || cleanedValue instanceof Date)
  ) {
    const formatOptions = column.formatter;
    if (formatOptions) {
      return formatDate(cleanedValue, formatOptions);
    } else {
      return formatDateDefault(cleanedValue);
    }
  }

  if (column.columnType === 'currency' && typeof cleanedValue === 'number') {
    const formatOptions = column.formatter;
    const { currency, ...remainingOptions } = formatOptions ?? {};
    if (!currency) {
      // no currency (e.g. 'EUR', 'USD') provided → default format without currency symbol
      return formatCurrencyDefault(cleanedValue, remainingOptions);
    }

    return formatCurrency(cleanedValue, currency, remainingOptions);
  }

  if (isDev()) {
    // eslint-disable-next-line no-console
    console.warn(`The value '${value}' can not be formatted!`);
  }
  return String(value);
}
